import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, AlertCircle, Shield, Phone, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import SEO from "@/components/SEO";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.09 } } };

/* @section: irs-notices */
export default function IRSNotices() {
  const { t } = useLanguage();
  const services = [{ icon: <FileText className="w-5 h-5" />, en: "Notice Review & Analysis", ko: "통지서 검토 및 분석", detailEn: "We carefully read and explain your IRS or state notice — in plain English and Korean.", detailKo: "IRS 또는 주정부 통지서를 영어와 한국어로 명확하게 설명해드립니다." },
    { icon: <AlertCircle className="w-5 h-5" />, en: "Response Preparation", ko: "답변서 준비", detailEn: "We draft and prepare professional written responses to the IRS or Franchise Tax Board.", detailKo: "IRS 또는 Franchise Tax Board에 전문적인 서면 답변서를 작성합니다." },
    { icon: <Shield className="w-5 h-5" />, en: "Balance Due Explanation", ko: "미납세액 설명", detailEn: "We explain what you owe, why, and what your payment or dispute options are.", detailKo: "미납액, 이유, 납부 또는 이의제기 옵션을 설명합니다." },
    { icon: <Phone className="w-5 h-5" />, en: "Penalty Notice Review", ko: "가산세 통지서 검토", detailEn: "Review penalty notices and evaluate options for penalty abatement requests when applicable.", detailKo: "가산세 통지서를 검토하고 해당 시 가산세 감면 요청 옵션을 평가합니다." },
    { icon: <FileText className="w-5 h-5" />, en: "IRS Account Transcript Review", ko: "IRS 계정 기록 검토", detailEn: "Pull and analyze your IRS account transcript to understand your full tax history.", detailKo: "IRS 계정 기록을 조회하고 분석하여 전체 세금 이력을 파악합니다." },
    { icon: <Shield className="w-5 h-5" />, en: "Enrolled Agent Representation", ko: "Enrolled Agent 대리인 서비스", detailEn: "As an EA, we can represent you directly before the IRS — you don't need to deal with them alone.", detailKo: "EA로서 IRS 앞에서 직접 대리할 수 있습니다 — 혼자 대응할 필요가 없습니다." },
  ];

  const commonNotices = [
    { code: "CP2000", en: "Underreported income — IRS found discrepancies in your return", ko: "신고 누락 소득 — IRS가 신고서의 불일치를 발견한 경우" },
    { code: "CP11 / CP12", en: "Math errors on your return — balance due or refund adjusted", ko: "신고서의 계산 오류 — 미납액 또는 환급액 조정" },
    { code: "CP14", en: "First notice of balance due — you owe taxes", ko: "미납세액 첫 번째 고지서" },
    { code: "CP501 / CP503", en: "Reminder of unpaid balance — with interest accruing", ko: "미납 잔액 독촉 — 이자 발생 중" },
    { code: "LT11 / Letter 1058", en: "Final notice before IRS levy — urgent action needed", ko: "IRS 압류 전 최종 통지 — 즉각 대응 필요" },
    { code: "4883C / 5071C", en: "Identity verification letter — confirm your return is yours", ko: "신원 확인 편지 — 신고서 본인 확인" },
  ];

  const steps = [
    { en: "Don't panic — most IRS notices are not audits", ko: "당황하지 마세요 — 대부분의 IRS 통지서는 감사가 아닙니다" },
    { en: "Read the notice carefully and note the response deadline", ko: "통지서를 꼼꼼히 읽고 응답 마감일을 메모하세요" },
    { en: "Contact NexBiz — bring the notice (or forward it to us)", ko: "NexBiz에 연락하세요 — 통지서를 가져오시거나 보내주세요" },
    { en: "We review, explain, and prepare your response", ko: "검토, 설명, 답변서 준비를 해드립니다" },
    { en: "We handle communication with the IRS on your behalf if needed", ko: "필요 시 IRS와의 소통을 대리합니다" },
  ];

  return (
    <>
      <SEO
        title="IRS Notice Help San Jose | Enrolled Agent Representation | NexBiz Tax Advisor"
        description="IRS Notice help and Tax Resolution services in San Jose. Enrolled Agent representation for IRS Letters, balance due, penalty notices, and IRS account issues in the Bay Area."
      />
      <section className="bg-primary py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-accent text-sm font-semibold uppercase tracking-wider mb-3">
              {t("IRS Notices & Tax Problem Support", "IRS 통지서 & 세금 문제 대응")}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              {t("Received an IRS or State Tax Notice?", "IRS 또는 주정부 세금 통지서를 받으셨나요?")}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-primary-foreground/70 text-lg max-w-2xl mx-auto">
              {t(
                "Don't face the IRS alone. As an Enrolled Agent, we have the authority to represent you directly.",
                "IRS를 혼자 상대하지 마세요. Enrolled Agent로서 직접 대리할 권한이 있습니다."
              )}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Urgent Banner */}
      <section className="bg-amber-50 border-y border-amber-200 py-5 px-4">
        <div className="container mx-auto max-w-4xl flex items-center gap-4">
          <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
          <p className="text-amber-800 text-sm font-medium">
            {t(
              "IRS notices have strict response deadlines — often 30 to 60 days. Contact us as soon as you receive a notice.",
              "IRS 통지서에는 엄격한 응답 기한이 있습니다 — 보통 30~60일. 통지서를 받는 즉시 연락하세요."
            )}
          </p>
          <Link to="/contact" className="ml-auto whitespace-nowrap inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-900 border border-amber-400 rounded-lg px-3 py-1.5 hover:bg-amber-100 transition-colors">
            {t("Contact Now", "지금 문의")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-3">
              {t("How We Help With IRS Notices", "IRS 통지서 대응 방법")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              {t("From notice review to full EA representation — we handle it professionally.", "통지서 검토부터 완전한 EA 대리까지 — 전문적으로 처리합니다.")}
            </motion.p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((s, i) => (
                <motion.div key={i} variants={fadeUp} className="bg-card border border-border rounded-xl p-5 hover:border-accent/40 hover:shadow-sm transition-all">
                  <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center text-primary mb-3">
                    {s.icon}
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t(s.en, s.ko)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(s.detailEn, s.detailKo)}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Notices */}
      <section className="bg-muted py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-10">
              {t("Common IRS Notices We Handle", "자주 처리하는 IRS 통지서")}
            </motion.h2>
            <div className="space-y-3">
              {commonNotices.map((n, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 bg-white rounded-xl border border-border p-4">
                  <span className="font-mono text-xs font-bold text-primary bg-primary/8 rounded px-2 py-1 shrink-0 mt-0.5">{n.code}</span>
                  <p className="text-sm text-foreground">{t(n.en, n.ko)}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-10">
              {t("What To Do When You Get an IRS Notice", "IRS 통지서를 받았을 때 해야 할 일")}
            </motion.h2>
            <div className="space-y-4">
              {steps.map((step, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 p-5 bg-card border border-border rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white text-sm font-bold shrink-0">{i + 1}</div>
                  <p className="text-foreground text-sm leading-relaxed pt-1">{t(step.en, step.ko)}</p>
                </motion.div>
              ))}
            </div>
            <motion.div variants={fadeUp} className="mt-8 text-center">
              <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity shadow-md">
                {t("Get Help With Your Notice", "통지서 대응 도움 받기")} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
